/**
 * RecruiterTestimonials — two candidate quotes placed right above the
 * pricing card on `/recruiter-visibility` so social proof lands
 * immediately before the buy decision.
 *
 * Apple-quiet: paired quote cards with subtle borders, no photos, no
 * gradient noise. Copy stays outcome-focused ("interviews doubled",
 * "moved from silence to offers") — no hyperbole, no product names in
 * the mouth of the candidate.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Star } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  fadeUp,
  displaySection,
  kicker,
  sectionWrap,
  sectionInner,
} from './shared';

const TESTIMONIALS = [
  {
    quote:
      'Interviews doubled in three weeks. Recruiters started replying to the same résumé I had been sending for months — the difference was the headline and the way my impact was framed.',
    name: 'Priya R.',
    role: 'Senior Product Manager · SaaS',
    initials: 'PR',
    tint: 'from-indigo-500/12 to-transparent',
    accent: 'text-indigo-600',
    ring: 'ring-indigo-200/50',
    testid: 'recruiter-testimonial-1',
  },
  {
    quote:
      'Went from silence to three shortlists inside a month. The peer benchmark showed exactly why my profile was invisible — I fixed those two things and my inbox changed overnight.',
    name: 'Arjun S.',
    role: 'Engineering Manager · Fintech',
    initials: 'AS',
    tint: 'from-rose-500/12 to-transparent',
    accent: 'text-rose-600',
    ring: 'ring-rose-200/50',
    testid: 'recruiter-testimonial-2',
  },
];

export const RecruiterTestimonials = () => (
  <section
    className={sectionWrap}
    aria-label="Candidate testimonials"
    id="testimonials"
    data-testid="recruiter-testimonials"
  >
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="mb-10 max-w-3xl">
        <p className={kicker}>What candidates say</p>
        <h2
          className={`${displaySection} mt-4 text-3xl sm:text-4xl lg:text-5xl`}
        >
          From invisible to{' '}
          <span className="text-primary">interviewing</span> — in weeks, not
          quarters.
        </h2>
      </motion.div>

      <div className="grid gap-5 md:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.name}
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 + i * 0.07 }}
          >
            <Card
              className={`relative h-full rounded-3xl border border-border/70 bg-card/80 backdrop-blur overflow-hidden`}
              data-testid={t.testid}
            >
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${t.tint}`}
              />
              <CardContent className="relative p-7 sm:p-8">
                <div className="flex items-center justify-between">
                  <Quote
                    size={22}
                    className={`${t.accent}`}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  <div
                    className="flex items-center gap-1"
                    aria-label="5 out of 5 stars"
                  >
                    {Array.from({ length: 5 }).map((_, si) => (
                      <Star
                        key={si}
                        size={12}
                        className="text-primary fill-primary"
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                </div>

                <p className="mt-5 text-[15px] sm:text-base leading-relaxed text-foreground/90">
                  &ldquo;{t.quote}&rdquo;
                </p>

                <div className="mt-6 flex items-center gap-3">
                  <Avatar
                    className={`h-9 w-9 ring-2 ${t.ring} shadow-sm`}
                  >
                    <AvatarFallback className="bg-secondary text-foreground text-[11px] font-medium">
                      {t.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-[13px] font-semibold leading-tight">
                      {t.name}
                    </p>
                    <p className="text-[12px] text-muted-foreground leading-tight mt-0.5">
                      {t.role}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default RecruiterTestimonials;
